import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";

import type { AuthCustomer, Cart, CreateOrderRequest, ShippingOption } from "@/lib/api-client";
import type { ShippingDistrict } from "@/lib/shipping-districts";
import { describeCartError } from "@/lib/cart";
import { usePlaceOrder, useShippingQuote, type PlaceOrderResult } from "@/lib/checkout";
import { formatXOF } from "@/lib/format";
import { ErrorBanner } from "@/components/cart/ErrorBanner";
import { CartTotals } from "@/components/cart/CartTotals";
import { StickyCartBar } from "@/components/cart/StickyCartBar";

export interface CheckoutFormProps {
  cart: Cart;
  districts: ShippingDistrict[];
  customer: AuthCustomer | null;
  /** Appelé au succès de `POST /checkout/orders`, avec tout ce dont la page de confirmation a besoin. */
  onOrderPlaced: (result: PlaceOrderResult) => void;
}

const FORM_ID = "checkout-form";

function pickDefaultShippingMethod(options: ShippingOption[]): string {
  const classic = options.find((o) => o.methodCode === "CLASSIC");
  if (classic) return classic.methodCode;
  return options[0]?.methodCode ?? "";
}

/**
 * Tunnel de commande (VVS_MODELE_INTERFACE.md §8), porté depuis
 * apps/web/components/cart/CheckoutForm.tsx. Écarts par rapport à l'original
 * (Server Actions + `useActionState`/`useFormStatus`) :
 *
 *  - `idempotencyKey` : un `useRef` calculé paresseusement au premier rendu
 *    remplace le `useState` + `useEffect(() => setIdempotencyKey(...), [])`
 *    de l'original. Ce dernier évitait un écart serveur/client à
 *    l'hydratation (Next.js) — non pertinent ici, SPA pure sans SSR. La règle
 *    métier reste strictement la même : généré UNE FOIS par tentative de
 *    commande, jamais régénéré à un re-rendu ni à un nouvel essai après
 *    erreur (condition de l'idempotence de `POST /checkout/orders`, §11.1).
 *  - `getShippingQuoteAction(districtId)` (Server Action) → `useShippingQuote(districtId)`
 *    (TanStack Query, src/lib/checkout.ts) : mêmes états (chargement/erreur/
 *    résultat), déclenché par le changement de `districtId` via `enabled`.
 *  - `placeOrderAction` (Server Action, lit un `FormData`, `redirect()` vers
 *    `/commande/confirmation?...`) → `usePlaceOrder()` (`useMutation`,
 *    src/lib/checkout.ts) ; au succès, `onOrderPlaced()` transmet le résultat
 *    au parent (`CommandePage`), qui navigue lui-même — cf. son commentaire
 *    pour le choix de `location.state` plutôt qu'une query string.
 *  - Les champs libres (nom, téléphone, quartier, adresse, repère, note) restent
 *    des inputs non contrôlés lus via `new FormData(event.currentTarget)` à la
 *    soumission — mêmes noms de champs et même validation que
 *    `placeOrderAction`, pour ne pas dériver du comportement d'origine.
 *    `districtId`/`shippingMethodCode` restent en état contrôlé (nécessaire au
 *    devis de livraison et à la liste des options).
 */
export function CheckoutForm({ cart, districts, customer, onOrderPlaced }: CheckoutFormProps) {
  const idempotencyKeyRef = useRef<string | null>(null);
  if (idempotencyKeyRef.current === null) {
    idempotencyKeyRef.current = crypto.randomUUID();
  }

  const [districtId, setDistrictId] = useState("");
  const [shippingMethodCode, setShippingMethodCode] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const shippingQuote = useShippingQuote(districtId);
  const placeOrder = usePlaceOrder();

  const selectedDistrict = useMemo(
    () => districts.find((d) => d.id === districtId) ?? null,
    [districts, districtId],
  );
  const selectedOption = useMemo(
    () => shippingQuote.data?.options.find((o) => o.methodCode === shippingMethodCode) ?? null,
    [shippingQuote.data, shippingMethodCode],
  );

  useEffect(() => {
    // §8.2 : « les frais s'affichent dès la commune choisie » — dès que le
    // devis arrive, sélectionne le premier mode par défaut (sauf si le mode
    // déjà choisi reste valable pour ce nouveau devis).
    if (!shippingQuote.data) return;
    setShippingMethodCode((current) =>
      shippingQuote.data!.options.some((option) => option.methodCode === current)
        ? current
        : pickDefaultShippingMethod(shippingQuote.data!.options),
    );
  }, [shippingQuote.data]);

  function handleDistrictChange(nextDistrictId: string) {
    setDistrictId(nextDistrictId);
    setShippingMethodCode("");
  }

  const subtotalMinor = cart.subtotal.amountMinor;
  const discountMinor = cart.discountTotal.amountMinor;
  const shippingMinor = selectedOption ? selectedOption.price.amountMinor : null;
  const grandTotalMinor = subtotalMinor - discountMinor + (shippingMinor ?? 0);

  const canSubmit = Boolean(districtId && shippingMethodCode) && !placeOrder.isPending;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError(null);

    const formData = new FormData(event.currentTarget);
    const recipientName = String(formData.get("recipientName") ?? "").trim();
    const phoneE164 = String(formData.get("phoneE164") ?? "").trim();
    const phoneSecondaire = String(formData.get("phoneSecondaire") ?? "").trim();
    const quartier = String(formData.get("quartier") ?? "").trim();
    const addressLine = String(formData.get("addressLine") ?? "").trim();
    const landmark = String(formData.get("landmark") ?? "").trim();
    const note = String(formData.get("customerNote") ?? "").trim();
    const acceptsTerms = formData.get("acceptsTerms");

    if (!districtId) return setFormError("Merci de choisir votre commune de livraison.");
    if (!recipientName) return setFormError("Merci d'indiquer le nom du destinataire.");
    if (!/^\+\d{8,15}$/.test(phoneE164)) {
      return setFormError("Merci de saisir un numéro de téléphone valide, au format +225XXXXXXXXXX.");
    }
    if (!shippingMethodCode) return setFormError("Merci de choisir un mode de livraison.");
    if (!acceptsTerms) return setFormError("Merci d'accepter les conditions générales de vente pour continuer.");

    const customerNote =
      [phoneSecondaire ? `Téléphone secondaire : ${phoneSecondaire}` : null, note || null]
        .filter((part): part is string => Boolean(part))
        .join(" — ") || undefined;

    const payload: CreateOrderRequest = {
      districtId,
      recipientName,
      phoneE164,
      quartier: quartier || undefined,
      addressLine: addressLine || undefined,
      landmark: landmark || undefined,
      shippingMethodCode,
      customerNote,
    };

    placeOrder.mutate(
      { payload, idempotencyKey: idempotencyKeyRef.current! },
      {
        onSuccess: (order) => {
          onOrderPlaced({
            order,
            recipientName,
            districtName: selectedDistrict ? `${selectedDistrict.name} — ${selectedDistrict.cityName}` : "",
            shippingMethodLabel: selectedOption?.label ?? "",
            itemCount: cart.itemCount,
          });
        },
        onError: (err) => setFormError(describeCartError(err)),
      },
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 pb-24 lg:grid-cols-12 lg:pb-0">
      <form id={FORM_ID} onSubmit={handleSubmit} className="flex flex-col gap-6 lg:col-span-8">
        {formError ? <ErrorBanner message={formError} /> : null}

        {/* ① Identification (§8.1) — la commande invité est explicitement supportée :
            pas d'OTP obligatoire ici, juste un rappel si déjà connecté. */}
        <section className="border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">① Identification</h2>
          {customer ? (
            <p className="mt-2 text-sm text-ink">
              ✔ Connecté·e : {[customer.firstName, customer.lastName].filter(Boolean).join(" ") || "Client"} ·{" "}
              {customer.phoneE164}
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted">
              Vous commandez sans compte. Indiquez vos coordonnées de livraison ci-dessous — aucune création de compte
              n&apos;est nécessaire.
            </p>
          )}
        </section>

        {/* ② Livraison (§8.2) */}
        <section className="flex flex-col gap-4 border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">② Livraison</h2>

          <Field label="Nom du destinataire" htmlFor="recipientName" required>
            <input
              id="recipientName"
              name="recipientName"
              type="text"
              required
              defaultValue={customer ? [customer.firstName, customer.lastName].filter(Boolean).join(" ") : ""}
              className={INPUT_CLASSES}
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Téléphone" htmlFor="phoneE164" required>
              <input
                id="phoneE164"
                name="phoneE164"
                type="tel"
                required
                placeholder="+2250700000000"
                pattern="^\+\d{8,15}$"
                title="Format international, ex. +2250700000000"
                defaultValue={customer?.phoneE164 ?? ""}
                className={INPUT_CLASSES}
              />
            </Field>
            <Field label="Téléphone secondaire (facultatif)" htmlFor="phoneSecondaire">
              <input id="phoneSecondaire" name="phoneSecondaire" type="tel" placeholder="+2250700000000" className={INPUT_CLASSES} />
            </Field>
          </div>

          <Field label="Commune" htmlFor="districtId" required hint="Obligatoire : c'est elle qui détermine les frais de livraison.">
            <select
              id="districtId"
              name="districtId"
              required
              value={districtId}
              onChange={(e) => handleDistrictChange(e.target.value)}
              className={INPUT_CLASSES}
            >
              <option value="">Choisir une commune…</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name} — {d.cityName}
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Quartier (facultatif)" htmlFor="quartier">
              <input id="quartier" name="quartier" type="text" className={INPUT_CLASSES} />
            </Field>
            <Field label="Adresse / description (facultatif)" htmlFor="addressLine">
              <input id="addressLine" name="addressLine" type="text" className={INPUT_CLASSES} />
            </Field>
          </div>

          <Field
            label="⚑ Repère"
            htmlFor="landmark"
            hint="Ex. « face à la pharmacie » — c'est souvent lui qui permet au livreur de vous trouver."
          >
            <input id="landmark" name="landmark" type="text" className={INPUT_CLASSES} />
          </Field>

          <div>
            <p className="mb-2 text-sm font-medium text-ink">Mode de livraison</p>
            {!districtId ? (
              <p className="text-sm text-muted">Choisissez d&apos;abord votre commune pour voir les modes disponibles.</p>
            ) : shippingQuote.isFetching ? (
              <p className="text-sm text-muted">Calcul des frais de livraison…</p>
            ) : shippingQuote.error ? (
              <ErrorBanner message={describeCartError(shippingQuote.error)} />
            ) : shippingQuote.data && shippingQuote.data.options.length > 0 ? (
              <ul className="flex flex-col gap-2">
                {shippingQuote.data.options.map((option) => {
                  const isDiscuss = option.methodCode === "DISCUSS_WITH_VENDOR";
                  return (
                    <li key={option.methodCode}>
                      <label className="flex cursor-pointer items-center justify-between gap-3 border border-line px-3 py-2 text-sm has-[:checked]:border-gold has-[:checked]:bg-gold/10">
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="shippingMethodCode"
                            value={option.methodCode}
                            checked={shippingMethodCode === option.methodCode}
                            onChange={() => setShippingMethodCode(option.methodCode)}
                            required
                          />
                          <span>
                            <span className="block font-medium text-ink">{option.label}</span>
                            <span className="block text-xs text-muted">{option.estimatedDelivery.label}</span>
                          </span>
                        </span>
                        <span className="whitespace-nowrap text-right font-semibold text-ink">
                          {isDiscuss ? (
                            <span className="text-xs font-semibold text-gold sm:text-sm">À convenir</span>
                          ) : option.freeShippingApplied ? (
                            <>
                              <span className="mr-1 text-xs text-muted line-through">
                                {option.originalPrice ? formatXOF(option.originalPrice.amountMinor) : null}
                              </span>
                              Offerte
                            </>
                          ) : (
                            formatXOF(option.price.amountMinor)
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted">Aucun mode de livraison disponible pour cette commune.</p>
            )}
          </div>
        </section>

        {/* ③ Paiement (§8.2 : un seul moyen actif au lot 1) */}
        <section className="flex flex-col gap-4 border border-line bg-white p-4">
          <h2 className="font-display text-lg font-semibold text-ink">③ Paiement</h2>

          <p className="border border-line bg-cream2 px-3 py-2 text-sm text-ink">💵 Paiement à la livraison (espèces)</p>

          <Field label="Note pour la livraison (facultatif)" htmlFor="customerNote">
            <textarea id="customerNote" name="customerNote" rows={3} className={INPUT_CLASSES} />
          </Field>

          <label className="flex items-start gap-2 text-sm text-ink">
            <input type="checkbox" name="acceptsTerms" required className="mt-1" />
            <span>J&apos;accepte les conditions générales de vente.</span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full border border-gold bg-gold px-6 py-3 text-sm font-medium uppercase tracking-wide text-navy transition-colors duration-150 hover:border-gold2 hover:bg-gold2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {placeOrder.isPending ? "Validation en cours…" : `Valider ma commande — ${formatXOF(grandTotalMinor)}`}
          </button>

          <p className="flex items-start gap-2 text-xs text-muted">
            <span aria-hidden="true">ⓘ</span>
            <span>Un conseiller vous appellera pour confirmer votre commande avant expédition.</span>
          </p>
        </section>
      </form>

      <aside className="lg:col-span-4">
        <div className="flex flex-col gap-4 border border-line bg-white p-4 lg:sticky lg:top-24">
          <p className="text-sm font-medium text-ink">
            {cart.itemCount} article{cart.itemCount > 1 ? "s" : ""}
          </p>
          <CartTotals
            subtotalMinor={subtotalMinor}
            discountMinor={discountMinor}
            shippingMinor={shippingMinor}
            totalMinor={grandTotalMinor}
            shippingLabel={
              selectedOption?.methodCode === "DISCUSS_WITH_VENDOR"
                ? "À convenir avec le vendeur"
                : undefined
            }
          />
          {selectedOption ? <p className="text-xs text-muted">🚚 {selectedOption.estimatedDelivery.label}</p> : null}
          {selectedOption?.methodCode === "DISCUSS_WITH_VENDOR" ? (
            <p className="border border-gold/40 bg-gold/10 p-2 text-xs text-ink">
              🤝 Le montant et le délai de livraison seront fixés directement avec le vendeur lors de l&apos;appel de confirmation.
            </p>
          ) : null}
          <p className="text-xs text-muted">💵 Payable à la livraison</p>
        </div>
      </aside>

      <StickyCartBar
        totalMinor={grandTotalMinor}
        label="Valider ma commande"
        action={{ type: "submit", formId: FORM_ID, disabled: !canSubmit }}
      />
    </div>
  );
}

const INPUT_CLASSES = "w-full border border-line bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:outline-none focus:border-navy";

function Field({
  label,
  htmlFor,
  required,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={htmlFor} className="text-sm font-medium text-ink">
        {label}
        {required ? <span className="text-golddark"> *</span> : null}
      </label>
      {children}
      {hint ? <p className="text-xs text-muted">{hint}</p> : null}
    </div>
  );
}
